<template>
  <div class="p-3 bg-white rounded shadow-sm" data-test="result-orchestrator" :data-orchestrator="`result_${contest.reference}`">
    <ResultOrchestratorGlobalActions
      :title="contest.title[locale as SupportedLocale]"
      :orchestrator-id="resultOrchestrator.id"
      :can-retry="resultOrchestrator.status !== 'finished' && resultOrchestrator.status !== 'mapping'"
      :can-reset="(resultOrchestrator.status !== 'finished' && resultOrchestrator.status !== 'mapping') || resultOrchestrator.error"
    />

    <div class="accent-line mt-2 mb-4"></div>
    <div v-if="resultOrchestrator.error">
      <details class="alert alert-warning mb-2">
        <summary class="fw-bold">{{ t("js.election_client.error.headers.an_error_occurred") }}</summary>
        <div>
          <p class="mb-0">{{ t("backend.e2e.voting_round_reports.show.error_body") }}</p>
        </div>
      </details>
    </div>

    <div v-if="result && result.resultData">
      <ResultOrchestratorResults
        :result-orchestrator="resultOrchestrator"
        :voting-round-report="votingRoundReport"
        :contest="contest"
        :result="result"
      />
      <div class="mt-4 small">
        <TurnoutTable
          :turnout="result.voterCounts"
          :hide-weight="contest.disregardWeights"
        />
      </div>

      <div class="mt-4 small">
        {{ t("backend.e2e.voting_round_reports.show.invalid_votes") }}: {{ result.voterCounts.invalidVotes }} <br />
        {{ t("backend.e2e.voting_round_reports.show.disabled_votes") }}: {{ result.voterCounts.disabledWeight }}
      </div>

      <ResultOrchestratorExternalVotes
        v-if="externalVotes"
        :external-votes="externalVotes"
      />

      <ResultOrchestratorCeremonyDetails
        :trustees="trustees"
        :result-orchestrator="resultOrchestrator"
      />
    </div>
    <div v-else-if="resultOrchestrator.status === 'mapping'">
      <WriteInMap :result-orchestrator="resultOrchestrator" :contest="contest" />
    </div>
    <div v-else>
      <ResultOrchestratorCalculatingResults
        :result-orchestrator="resultOrchestrator"
        :trustees="trustees"
        :threshold="threshold"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ResultOrchestratorGlobalActions from "@/components/backend/result_orchestrator/ResultOrchestratorGlobalActions.vue";
import ResultOrchestratorExternalVotes from "@/components/backend/result_orchestrator/ResultOrchestratorExternalVotes.vue";
import ResultOrchestratorResults from "@/components/backend/result_orchestrator/ResultOrchestratorResults.vue";
import ResultOrchestratorCeremonyDetails from "@/components/backend/result_orchestrator/ResultOrchestratorCeremonyDetails.vue";
import ResultOrchestratorCalculatingResults from "@/components/backend/result_orchestrator/ResultOrchestratorCalculatingResults.vue";
import { flattenOptions } from "@/entrypoints/shared/contest_utilities";
import TurnoutTable from "@/components/backend/live/TurnoutTable.vue";
import WriteInMap from "./WriteInMap.vue";
import { useI18n } from "vue-i18n";
import type {
  ConferenceContest,
  ElectaTrustee,
  PropType,
  E2E,
  ConferenceVotingRoundReport,
  ConferenceOption,
  Result,
  SupportedLocale,
} from "@/types";

const { locale, t } = useI18n();

const props = defineProps({
  resultOrchestrator: {
    type: Object as PropType<E2E.ResultOrchestrator>,
    required: true,
  },
  votingRoundReport: {
    type: Object as PropType<ConferenceVotingRoundReport>,
    required: true,
  },
  contest: {
    type: Object as PropType<ConferenceContest>,
    required: true,
  },
  trustees: {
    type: Array as PropType<Array<ElectaTrustee>>,
    required: true,
  },
  threshold: {
    type: Number,
    required: true,
  },
});

const result = computed<Result>(() => props.resultOrchestrator.result as Result);

const externalVotes = computed((): undefined | Record<string, number | Record<string, number>> => {
  let externalVotes = {}
  const resultData = (result.value as Result).resultData.externalVotes?.[props.contest.reference];
  if (!resultData) return;

  Object.values(optionsFlattened.value).forEach((option: ConferenceOption) => {
    if (!option.reference || option.reference == "blank") return;
    externalVotes[option.title[locale.value] || Object.values(option.title)[0]] = resultData[option.reference];
  });

  if (props.contest.blankOption !== "disabled") externalVotes[t("js.standard_option.blank")] = resultData["blank"];

  return externalVotes;
});

const optionsFlattened = computed(() => flattenOptions(props.contest.options));
</script>
